import { Fragment, MouseEvent, useLayoutEffect, useState } from 'react';
import { ColorResult, SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { Box, Popover, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Swiper, SwiperSlide } from 'swiper/react';

import ArrowsLeftRightIcon from '~/assets/icons/svgs/arrows-left-right.svg?react';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import useBuilderBlockMaxWidth from '~/hooks/useBuilderBlockMaxWidth';
import { COLORS } from '~/styles/colors';
import { getIsValidHexColor } from '~/utils/validation/color';

const getDefaultSelectedIndex = ({
  initialColors,
  selectedColors,
}: {
  initialColors: string[][];
  selectedColors: string[];
}) => {
  if (!selectedColors?.length) {
    return -1;
  }

  return initialColors.findIndex((colors) => {
    return colors.every((color, index) => {
      return color === selectedColors?.[index];
    });
  });
};

type MultiColorPickerProps = {
  initialColors: string[][];
  setColorValues: (colors: string[]) => void;
  blurFunction?: (colors: string[]) => void;
  selectedTemplate?: number;
  getReplacedInitialColor?: () => string[];
  onSketchPickerChange: (colors: string[]) => void;
  selectedColors: string[];
};

export default function MultiColorPicker({
  initialColors,
  setColorValues,
  selectedTemplate,
  blurFunction,
  onSketchPickerChange,
  getReplacedInitialColor,
  selectedColors,
}: MultiColorPickerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const defaultSelectedIndex = getDefaultSelectedIndex({ initialColors, selectedColors });

  const [colorsArray, setColorsArray] = useState<string[][]>(initialColors);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex > -1 ? defaultSelectedIndex : 0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popupIndex, setPopupIndex] = useState(0);
  const [colorErrors, setColorErrors] = useState<(string | null)[]>(Array(initialColors.length).fill(null));

  const desktopContainerWidth = useBuilderBlockMaxWidth();

  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;
  const inputContainerStyles = isDesktop ? {} : { flexDirection: 'column', alignItems: 'center' };
  const isColorError = colorErrors.some((error) => !!error);

  const handleSupportPopoverClick = (indexSub: number, event: MouseEvent<HTMLElement>) => {
    setPopupIndex(indexSub);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setColorValues(colorsArray[selectedIndex]);
    setAnchorEl(null);
  };

  const handleChangeInput = (index: number, value: string) => {
    const updatedColorsArray = Object.values({
      ...colorsArray,
      [selectedIndex]: Object.values({ ...colorsArray[selectedIndex], [index]: value }),
    }) as string[][];

    const newErrors = [...colorErrors];
    if (!getIsValidHexColor(value)) {
      newErrors[index] = t('errors:invalidHexCode');
    } else {
      newErrors[index] = null;
    }
    setColorErrors(newErrors);
    setColorsArray(updatedColorsArray);
  };

  const handleSwapColorClick = () => {
    if (colorsArray[selectedIndex].length === 2) {
      const swappedColors = [...colorsArray[selectedIndex]].reverse();
      const updatedColorsArray = Object.values({
        ...colorsArray,
        [selectedIndex]: swappedColors,
      }) as string[][];
      setColorsArray(updatedColorsArray);
      setColorValues(swappedColors);
    }
  };

  const labelTextByColors = (length: number): string[] => {
    switch (length) {
      case 1:
        return [t('template1:primary')];
      case 2:
        return [t('template1:primary'), t('template1:secondary')];
      case 3:
        return [t('template1:bg'), t('template1:bgLink'), t('template1:colorLink')];
      default:
        return [t('template1:primary')];
    }
  };

  const handleSelectColorIndex = (index: number) => {
    setSelectedIndex(index);
    setColorValues(colorsArray[index]);
    if (blurFunction) blurFunction(colorsArray[index]);
  };

  useLayoutEffect(() => {
    if (getReplacedInitialColor && defaultSelectedIndex < 0) {
      const initial = [...colorsArray];
      initial[selectedIndex] = getReplacedInitialColor();
      setColorsArray(initial);
    }
  }, [selectedTemplate]);

  return (
    <>
      <Box sx={{ maxWidth: isDesktop ? desktopContainerWidth : 'calc(100dvw - 64px)' }}>
        <Swiper spaceBetween={8} slidesPerView="auto">
          {colorsArray?.map((item, index) => {
            const isSelected =
              item?.every((element, i) => element === colorsArray[selectedIndex][i]) && index === selectedIndex;

            return (
              <SwiperSlide key={index} style={{ maxWidth: 148, borderRadius: '20px' }}>
                <SwatchContainer key={index} onClick={() => handleSelectColorIndex(index)} $isSelected={isSelected}>
                  {item?.map((subColor, indexSub) => (
                    <ColorSwatch key={indexSub} $color={subColor} $count={item.length} />
                  ))}
                </SwatchContainer>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        sx={{
          backgroundColor: COLORS.lightGray,
          padding: '16px',
          gap: '16px',
          mt: 2,
          borderRadius: '20px',
          ...inputContainerStyles,
          paddingBottom: isColorError ? '36px' : '16px',
        }}
      >
        {colorsArray[selectedIndex].map((color, indexSub, arr) => (
          <Fragment key={indexSub}>
            <TextInput
              label={labelTextByColors(colorsArray[selectedIndex].length)[indexSub]}
              value={color}
              onChange={(e) => handleChangeInput(indexSub, e.target.value)}
              fullWidth
              containerSx={arr.length === 1 ? { maxWidth: '300px' } : undefined}
              onBlur={() => {
                if (blurFunction) blurFunction(colorsArray[0]);
                setColorValues(colorsArray[selectedIndex]);
              }}
              error={!!colorErrors[indexSub]}
              helperText={colorErrors[indexSub]}
              absoluteHelperText
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment
                      position="end"
                      sx={{ cursor: 'pointer' }}
                      onClick={(event) => handleSupportPopoverClick(indexSub, event)}
                    >
                      <Box
                        style={{ backgroundColor: color }}
                        sx={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '2px',
                          border: `1px solid ${COLORS.black}`,
                        }}
                      />
                    </InputAdornment>
                    <Popover
                      id={`${id}${color}`}
                      open={open && popupIndex === indexSub}
                      anchorEl={anchorEl}
                      onClose={handleClosePopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: -20,
                        horizontal: 'right',
                      }}
                    >
                      <Box width={220}>
                        <SketchPicker
                          color={color}
                          onChange={(newColor: ColorResult) => handleChangeInput(popupIndex, newColor.hex)}
                          onChangeComplete={() => {
                            onSketchPickerChange(colorsArray[selectedIndex]);
                            blurFunction?.(colorsArray[selectedIndex]);
                          }}
                        />
                      </Box>
                    </Popover>
                  </>
                ),
              }}
            />
            {indexSub === 0 && colorsArray[selectedIndex].length === 2 && (
              <IconButton
                aria-label="swap"
                sx={{
                  width: '40px',
                  height: '40px',
                  rotate: isDesktop ? undefined : '90deg',
                  backgroundColor: COLORS.primary,
                  '&:hover': {
                    backgroundColor: COLORS.primary,
                  },
                }}
                disableRipple
                onClick={handleSwapColorClick}
              >
                <ArrowsLeftRightIcon />
              </IconButton>
            )}
          </Fragment>
        ))}
      </Box>
    </>
  );
}

const ColorSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$color' && prop !== '$count',
})<{ $color: string; $count: number }>(({ $color, $count }) => ({
  display: 'flex',
  flex: 1,
  backgroundColor: $color,
  height: 28,
  minWidth: $count === 3 ? '34px' : '56px',
  boxSizing: 'border-box',
  border: $color?.toLowerCase() === '#ffffff' || $color === 'white' ? `1px solid ${COLORS.black}` : undefined,
}));

const SwatchContainer = styled('button')<{ $isSelected: boolean }>(({ $isSelected }) => ({
  backgroundColor: COLORS.white,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `2px solid ${$isSelected ? COLORS.primary : COLORS.grey200}`,
  borderRadius: '4px',
  padding: '14px',
  maxWidth: 150,
  flex: 1,
  gap: '5px',
  overscrollBehavior: 'contain',
  width: '100%',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));
